import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import ScrollToTop from "./components/utils/ScrollToTop";
import { useEffect, useState } from "react";
import LoadingScreen from "./components/utils/LoadingScreen";
import Index from "./pages/Index";
import Services from "./pages/Services";
import About from "./pages/About";
import Contact from "./pages/Contact";
// Case studies page removed
import NotFound from "./pages/NotFound";
import Blog from './pages/Blog';

// Import service detail pages
import DataAnalytics from "./pages/services/DataAnalytics";
import MachineLearning from "./pages/services/MachineLearning";
import IoTSolutions from "./pages/services/IoTSolutions";
import ArtificialIntelligence from "./pages/services/ArtificialIntelligence";
import CloudInfrastructure from "./pages/services/CloudInfrastructure";
import IndustrialAutomation from "./pages/services/IndustrialAutomation";
import AutonomousAgents from "./pages/services/AutonomousAgents";
import AIInfrastructure from "./pages/services/AIInfrastructure";
import AIModels from "./pages/services/AIModels";
import ComputerVision from "./pages/services/ComputerVision";
import RoboticsAutomation from "./pages/services/RoboticsAutomation";

// Create context for page transitions
const queryClient = new QueryClient();

// AnimatedRoutes Component with framer-motion
const AnimatedRoutes = () => {
  const location = useLocation();
  
  // Enhanced scroll behavior
  useEffect(() => {
    // Smooth scroll polyfill
    document.documentElement.style.scrollBehavior = 'smooth';
    
    return () => {
      document.documentElement.style.scrollBehavior = '';
    };
  }, []);

  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Index />} />
        <Route path="/services" element={<Services />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        {/* Case studies route removed */}
        <Route path="/blog" element={<Blog />} />
        
        {/* Service detail routes */}
        <Route path="/services/data-analytics" element={<DataAnalytics />} />
        <Route path="/services/machine-learning" element={<MachineLearning />} />
        <Route path="/services/iot-solutions" element={<IoTSolutions />} />
        <Route path="/services/artificial-intelligence" element={<ArtificialIntelligence />} />
        <Route path="/services/cloud-infrastructure" element={<CloudInfrastructure />} />
        <Route path="/services/industrial-automation" element={<IndustrialAutomation />} />
        <Route path="/services/autonomous-agents" element={<AutonomousAgents />} />
        <Route path="/services/ai-infrastructure" element={<AIInfrastructure />} />
        <Route path="/services/ai-models" element={<AIModels />} />
        <Route path="/services/computer-vision" element={<ComputerVision />} />
        <Route path="/services/robotics-automation" element={<RoboticsAutomation />} />
        
        {/* Catch-all route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AnimatePresence>
  );
};

const App = () => {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <AnimatePresence mode="wait">
          {isLoading ? (
            <LoadingScreen 
              key="loading-screen"
              minLoadTime={1500} 
              onLoadComplete={() => setIsLoading(false)} 
            />
          ) : (
            <BrowserRouter key="app-content">
              <ScrollToTop />
              <AnimatedRoutes />
            </BrowserRouter>
          )}
        </AnimatePresence>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
